import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import {
  plannerService,
  type CreateTaskInput,
  type PlannerStatus,
  type TaskUpdate,
} from '@/services/plannerService';

export const PLANNER_QUERY_KEY = ['planner'] as const;

export function usePlan(tenantId?: string | null, billingPeriod?: string) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'plan', tenantId, billingPeriod],
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerService.plan(tenantId, billingPeriod);
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

export function useLedger(tenantId?: string | null) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'ledger', tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerService.ledger(tenantId);
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

/** Teamwork's staff for the Assignee list (E5). Its own key: planner
 *  mutations invalidate PLANNER_QUERY_KEY, and the backend caches 5 min. */
export function useAssignees() {
  return useQuery({
    queryKey: ['planner-assignees'],
    queryFn: () => plannerService.assignees(),
    staleTime: 300_000,
  });
}

export function useCatalog(activeOnly = false) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'catalog', activeOnly],
    queryFn: () => plannerService.catalog(activeOnly),
    staleTime: 300_000,
  });
}

export function useCreateTask(tenantId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskInput) => plannerService.createTask(tenantId as string, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLANNER_QUERY_KEY });
      message.success('Task added to the plan.');
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't add the task.";
      message.error(detail);
    },
  });
}

export function useArchivedTasks(tenantId?: string | null) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'archived', tenantId],
    queryFn: () => plannerService.archivedTasks(tenantId as string),
    enabled: Boolean(tenantId),
  });
}

export function useRestoreTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannerService.restoreTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PLANNER_QUERY_KEY });
      message.success('Restored — re-sync to push it back to Teamwork.');
    },
    onError: () => message.error('Restore failed'),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaskUpdate }) =>
      plannerService.updateTask(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANNER_QUERY_KEY }),
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail ?? 'Update failed');
    },
  });
}

export function useSetTaskStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PlannerStatus }) =>
      plannerService.setStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANNER_QUERY_KEY }),
    onError: () => message.error("Couldn't move the task right now."),
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannerService.deleteTask(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANNER_QUERY_KEY }),
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail || "Couldn't delete the task.");
    },
  });
}

export function usePushTaskToTeamwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannerService.pushTaskToTeamwork(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: PLANNER_QUERY_KEY });
      if (res.planner_write === "failed") {
        message.warning("Task created in Teamwork, but the Planner didn't record the link yet. Core has it, so pushing again won't duplicate it.");
        return;
      }
      message.success(res.skipped ? "Already synced to Teamwork." : "Task created in Teamwork.");
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail || "Couldn't sync the task to Teamwork.");
    },
  });
}

/**
 * The Teamwork push with a per-task in-flight guard (E7), shared by every
 * caller (row buttons, mobile card, bulk). `sync(id)` does nothing while that
 * task's push is running: the ref is updated synchronously, so two clicks
 * before React re-renders still send one push. `syncingIds` drives the busy
 * buttons. `sync` rejects on error (the hook's onError already toasts).
 */
export function useTeamworkSync() {
  const { mutateAsync } = usePushTaskToTeamwork();
  const inFlight = useRef(new Set<string>());
  const [syncingIds, setSyncingIds] = useState<ReadonlySet<string>>(() => new Set());
  const sync = useCallback(async (id: string) => {
    if (inFlight.current.has(id)) return;
    const publish = () => setSyncingIds(new Set(inFlight.current));
    inFlight.current.add(id);
    publish();
    try {
      await mutateAsync(id);
    } finally {
      inFlight.current.delete(id);
      publish();
    }
  }, [mutateAsync]);
  return { syncingIds, sync };
}

export function useSetBudget(tenantId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billingPeriod, budgetPoints }: { billingPeriod: string; budgetPoints: number }) =>
      plannerService.setBudget(tenantId as string, billingPeriod, budgetPoints),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANNER_QUERY_KEY }),
    onError: () => message.error("Couldn't update the budget."),
  });
}

export function useOffsite(tenantId?: string | null) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'offsite', tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerService.offsiteList(tenantId);
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

export function useCreateOffsite(tenantId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { url: string; anchor?: string; target_url?: string; notes?: string }) =>
      plannerService.offsiteCreate(tenantId as string, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'offsite'] });
      message.success('Link added.');
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't add the link.";
      message.error(detail);
    },
  });
}

export function useUpdateOffsite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof plannerService.offsiteUpdate>[1] }) =>
      plannerService.offsiteUpdate(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'offsite'] }),
    onError: () => message.error("Couldn't update the link."),
  });
}

export function useDeleteOffsite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannerService.offsiteDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'offsite'] }),
    onError: () => message.error("Couldn't remove the link."),
  });
}

export function useFeedback(status?: string) {
  return useQuery({
    queryKey: [...PLANNER_QUERY_KEY, 'feedback', status ?? 'all'],
    queryFn: () => plannerService.feedbackList(status),
    staleTime: 15_000,
  });
}

export function useCreateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof plannerService.feedbackCreate>[0]) =>
      plannerService.feedbackCreate(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'feedback'] });
      message.success('Feedback submitted.');
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't submit feedback.";
      message.error(detail);
    },
  });
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof plannerService.feedbackUpdate>[1] }) =>
      plannerService.feedbackUpdate(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'feedback'] }),
    onError: () => message.error("Couldn't update the feedback."),
  });
}

// ---- groups ----
const GROUPS_KEY = [...PLANNER_QUERY_KEY, 'groups'] as const;

export function useGroups() {
  return useQuery({ queryKey: GROUPS_KEY, queryFn: () => plannerService.groups(), staleTime: 15_000 });
}
export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; notes?: string }) => plannerService.groupCreate(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: GROUPS_KEY }); message.success('Group created.'); },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Couldn't create the group."),
  });
}
export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannerService.groupDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
    onError: () => message.error("Couldn't delete the group."),
  });
}
export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId: string }) => plannerService.groupAddMember(id, tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
    onError: () => message.error("Couldn't add the client."),
  });
}
export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId: string }) => plannerService.groupRemoveMember(id, tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
    onError: () => message.error("Couldn't remove the client."),
  });
}

// ---- approvals ----
const APPROVALS_KEY = [...PLANNER_QUERY_KEY, 'approvals'] as const;

export function useApprovals(status?: string) {
  return useQuery({ queryKey: [...APPROVALS_KEY, status ?? 'all'], queryFn: () => plannerService.approvals(status), staleTime: 15_000 });
}
export function useCreateApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tenant_id: string; billing_period: string; note?: string }) => plannerService.approvalCreate(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: APPROVALS_KEY }); message.success('Sent for approval.'); },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Couldn't send for approval."),
  });
}
export function useResolveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: Parameters<typeof plannerService.approvalResolve>[1]; note?: string }) =>
      plannerService.approvalResolve(id, status, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: APPROVALS_KEY }),
    onError: () => message.error("Couldn't update the approval."),
  });
}

export function useCreateCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; service_bucket?: string; points_per_unit?: number }) =>
      plannerService.createCatalog(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PLANNER_QUERY_KEY, 'catalog'] });
      message.success('Task type added.');
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't add the task type.";
      message.error(detail);
    },
  });
}
